import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import masterService from '../../services/masterService';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';

export const CentrePanelMappingPage = ({ showToast }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Centre Selection
  const [centres, setCentres] = useState([]);
  const [selectedCentreId, setSelectedCentreId] = useState(
    searchParams.get('centreId') ? parseInt(searchParams.get('centreId'), 10) : ''
  );

  // Available Panels & Search
  const [allPanels, setAllPanels] = useState([]);
  const [panelSearchQuery, setPanelSearchQuery] = useState('');
  const [panelTypeFilter, setPanelTypeFilter] = useState('all');
  const [selectedPanelIds, setSelectedPanelIds] = useState([]);

  // Effective Dates for Mapping
  const todayStr = new Date().toISOString().split('T')[0];
  const [effectiveFrom, setEffectiveFrom] = useState(todayStr);
  const [effectiveTo, setEffectiveTo] = useState('');

  // Existing Mappings
  const [mappings, setMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [mappingSearch, setMappingSearch] = useState('');
  const [mappingStatusFilter, setMappingStatusFilter] = useState('all');

  // Saving / Actions
  const [savingMapping, setSavingMapping] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'deactivate' | 'activate', mapping: {...} }
  const [actionProcessing, setActionProcessing] = useState(false);
  const [detailMapping, setDetailMapping] = useState(null);

  // 1. Fetch Centres & Panels on initial mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          masterService.getCentres({ isActive: true }),
          masterService.getPanels({ isActive: true })
        ]);

        const centresList = cRes.success && cRes.data ? cRes.data : [];
        setCentres(centresList);

        const panelsList = pRes.success && pRes.data ? pRes.data : [];
        setAllPanels(panelsList);

        // Auto-select first centre if none preselected
        if (!selectedCentreId && centresList.length > 0) {
          setSelectedCentreId(centresList[0].centreId);
          setSearchParams({ centreId: centresList[0].centreId });
        }
      } catch (err) {
        if (showToast) showToast('Failed to load centres or panels.', 'error');
      }
    };
    fetchDropdownData();
  }, []);

  // 2. Fetch Mappings whenever selected Centre changes
  const fetchMappings = useCallback(async () => {
    if (!selectedCentreId) {
      setMappings([]);
      return;
    }
    setLoadingMappings(true);
    try {
      const res = await masterService.getCentrePanelMappings({
        centreId: selectedCentreId,
        isActive: mappingStatusFilter === 'all' ? undefined : mappingStatusFilter === 'active',
        search: mappingSearch.trim() || undefined
      });
      if (res.success && res.data) {
        setMappings(res.data);
      } else {
        setMappings([]);
      }
    } catch (err) {
      if (showToast) showToast('Failed to fetch centre mappings.', 'error');
      setMappings([]);
    } finally {
      setLoadingMappings(false);
    }
  }, [selectedCentreId, mappingStatusFilter, mappingSearch, showToast]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  // Handle Centre Dropdown Change
  const handleCentreChange = (newCentreId) => {
    const cid = parseInt(newCentreId, 10);
    setSelectedCentreId(cid);
    setSelectedPanelIds([]);
    setSearchParams({ centreId: cid });
  };

  // Set of actively mapped panel IDs for the selected centre
  const activelyMappedPanelIds = useMemo(() => {
    const ids = new Set();
    mappings.forEach(m => {
      if (m.isActive) {
        ids.add(m.panelId);
      }
    });
    return ids;
  }, [mappings]);

  // Filtered centre mappings list
  const filteredMappings = useMemo(() => {
    return mappings.filter(m => {
      if (mappingStatusFilter === 'active' && !m.isActive) return false;
      if (mappingStatusFilter === 'inactive' && m.isActive) return false;
      if (mappingSearch.trim()) {
        const q = mappingSearch.toLowerCase();
        const code = (m.panelCode || '').toLowerCase();
        const name = (m.panelName || '').toLowerCase();
        if (!code.includes(q) && !name.includes(q)) return false;
      }
      return true;
    });
  }, [mappings, mappingStatusFilter, mappingSearch]);

  // Centralized Pagination for centre mappings
  const mappingPagination = usePagination({
    items: filteredMappings,
    resetDeps: [selectedCentreId, mappingSearch, mappingStatusFilter],
  });

  // Filtered available panels
  const filteredAvailablePanels = useMemo(() => {
    return allPanels.filter(p => {
      // Panel Type filter
      if (panelTypeFilter !== 'all' && (p.panelType || 'Cash').toLowerCase() !== panelTypeFilter.toLowerCase()) {
        return false;
      }
      // Search query (code or name)
      if (panelSearchQuery.trim()) {
        const q = panelSearchQuery.toLowerCase();
        const code = (p.panelCode || '').toLowerCase();
        const name = (p.panelName || '').toLowerCase();
        if (!code.includes(q) && !name.includes(q)) return false;
      }
      return true;
    });
  }, [allPanels, panelTypeFilter, panelSearchQuery]);

  // Toggle Panel Selection
  const togglePanelSelection = (panelId) => {
    if (activelyMappedPanelIds.has(panelId)) return; // Already mapped
    setSelectedPanelIds(prev => 
      prev.includes(panelId) ? prev.filter(id => id !== panelId) : [...prev, panelId]
    );
  };

  // Select All filtered unmapped panels
  const handleSelectAllFiltered = () => {
    const unmappedIds = filteredAvailablePanels
      .filter(p => !activelyMappedPanelIds.has(p.panelId))
      .map(p => p.panelId);
    setSelectedPanelIds(Array.from(new Set([...selectedPanelIds, ...unmappedIds])));
  };

  // Clear Selection
  const handleClearSelection = () => {
    setSelectedPanelIds([]);
  };

  // Remove single selected panel chip
  const handleRemoveChip = (panelId) => {
    setSelectedPanelIds(prev => prev.filter(id => id !== panelId));
  };

  // Save Mapping (Bulk or Single)
  const handleSaveMapping = async () => {
    if (!selectedCentreId) {
      if (showToast) showToast('Please select a valid Centre first.', 'error');
      return;
    }
    if (selectedPanelIds.length === 0) {
      if (showToast) showToast('Please select at least one Panel to map.', 'warning');
      return;
    }
    if (effectiveFrom && effectiveTo && effectiveTo < effectiveFrom) {
      if (showToast) showToast('Effective To date cannot be earlier than Effective From date.', 'error');
      return;
    }

    setSavingMapping(true);
    try {
      const payload = {
        centreId: selectedCentreId,
        panelIds: selectedPanelIds,
        fromDate: effectiveFrom || null,
        toDate: effectiveTo || null
      };

      const res = await masterService.bulkCreateCentrePanelMappings(payload);
      if (res.success) {
        if (showToast) showToast(res.message || 'Panel mapping saved successfully!', 'success');
        setSelectedPanelIds([]);
        fetchMappings();
      } else {
        if (showToast) showToast(res.message || 'Failed to save mapping.', 'error');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Error saving mapping.';
      if (showToast) showToast(msg, 'error');
    } finally {
      setSavingMapping(false);
    }
  };

  // Toggle Mapping Status (Activate / Deactivate)
  const handleConfirmStatusChange = async () => {
    if (!confirmAction) return;
    const { mapping, type } = confirmAction;
    setActionProcessing(true);
    try {
      const isActivating = type === 'activate';
      const toDate = isActivating ? null : todayStr;
      const res = await masterService.updateCentrePanelMappingStatus(mapping.id, isActivating, toDate);
      if (res.success) {
        if (showToast) showToast(`Mapping for "${mapping.panelName}" ${isActivating ? 'activated' : 'deactivated'} successfully.`, 'success');
        fetchMappings();
      } else {
        if (showToast) showToast(res.message || 'Action failed.', 'error');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Action failed.';
      if (showToast) showToast(msg, 'error');
    } finally {
      setActionProcessing(false);
      setConfirmAction(null);
    }
  };

  const selectedCentreObj = centres.find(c => c.centreId === selectedCentreId);

  return (
    <div className="page-container fade-in">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb">
        <button type="button" onClick={() => navigate('/dashboard')}>
          Home
        </button>
        <span className="breadcrumb-divider">/</span>
        <button type="button" onClick={() => navigate('/masters')}>
          Masters
        </button>
        <span className="breadcrumb-divider">/</span>
        <span>Commercial Mappings</span>
        <span className="breadcrumb-divider">/</span>
        <span className="breadcrumb-active">Centre – Panel Commercial Mapping</span>
      </div>

      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: '1.25rem' }}>
        <div className="page-title-group">
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <span>🏢</span>
            <span>Centre – Panel Commercial Mapping</span>
          </h1>
          <span className="page-subtitle">
            Configure commercial referral, corporate, and partner panels permitted to register patient orders at this laboratory centre.
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/masters/panel-rates')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
          >
            <span>💰</span>
            <span>Panel Rate Cards</span>
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/masters/panels')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
          >
            <span>📋</span>
            <span>Panel Master</span>
          </button>
        </div>
      </div>

      {/* 1. Centre Selector & Validity Glass Card */}
      <div className="glass-card" style={{ padding: '1.25rem 1.5rem', marginBottom: '1.5rem', borderLeft: '4px solid var(--primary-color)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem', alignItems: 'flex-end' }}>
          {/* Centre Dropdown */}
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              Select Laboratory Centre <span style={{ color: 'var(--danger-color)' }}>*</span>
            </label>
            <select
              className="form-control"
              value={selectedCentreId}
              onChange={(e) => handleCentreChange(e.target.value)}
              id="centre-panel-centre-select"
              style={{ fontWeight: 500, fontSize: '0.95rem' }}
            >
              <option value="">-- Choose Diagnostic / Processing Centre --</option>
              {centres.map(c => (
                <option key={c.centreId} value={c.centreId}>
                  {c.centreCode} - {c.centreName} {c.city ? `(${c.city})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Effective From */}
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">
              Effective From
            </label>
            <input
              type="date"
              className="form-control"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              id="mapping-effective-from"
            />
          </div>

          {/* Effective To */}
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">
              Effective To <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>(Optional / Open)</span>
            </label>
            <input
              type="date"
              className="form-control"
              value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
              id="mapping-effective-to"
            />
          </div>

          {/* Centre Summary Info */}
          <div style={{ padding: '0.65rem 1rem', background: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
            <div style={{ color: 'var(--text-muted)' }}>Selected Centre Info:</div>
            <div style={{ fontWeight: 600, color: 'var(--primary-color)', marginTop: '0.15rem' }}>
              {selectedCentreObj ? `${selectedCentreObj.centreName} (${selectedCentreObj.centreCode})` : 'None Selected'}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              {selectedCentreObj?.city ? `${selectedCentreObj.city}, ${selectedCentreObj.state || 'India'}` : 'Location configured'} • Active Panels: {activelyMappedPanelIds.size}
            </div>
          </div>
        </div>
      </div>

      {/* 2. Top Section: Available Panels Multi-Select & Assignment Drawer */}
      <div className="glass-card" style={{ padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Assign Commercial Panels to Centre
            </h3>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Search and check panels below to map them to {selectedCentreObj?.centreName || 'the selected centre'}.
            </span>
          </div>

          {/* Search & Filter Controls */}
          <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: '260px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search panel code / name..."
                value={panelSearchQuery}
                onChange={(e) => setPanelSearchQuery(e.target.value)}
                id="search-available-panels"
                style={{ paddingRight: '2rem' }}
              />
              {panelSearchQuery && (
                <button
                  type="button"
                  onClick={() => setPanelSearchQuery('')}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  ✕
                </button>
              )}
            </div>

            <select
              className="form-control"
              value={panelTypeFilter}
              onChange={(e) => setPanelTypeFilter(e.target.value)}
              style={{ width: '140px' }}
            >
              <option value="all">All Types</option>
              <option value="cash">Cash</option>
              <option value="corporate">Corporate</option>
              <option value="hospital">Hospital</option>
              <option value="referral">Referral</option>
            </select>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleSelectAllFiltered}
              title="Select all currently filtered unmapped panels"
              style={{ padding: '0.45rem 0.85rem', fontSize: '0.85rem' }}
            >
              Select Filtered
            </button>
            {selectedPanelIds.length > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClearSelection}
                style={{ padding: '0.45rem 0.85rem', fontSize: '0.85rem' }}
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>

        {/* Panel Multi-Selection Checklist */}
        <div style={{
          maxHeight: '260px',
          overflowY: 'auto',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '0.75rem',
          backgroundColor: 'var(--bg-secondary)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
          gap: '0.65rem'
        }}>
          {filteredAvailablePanels.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              No panels found matching the current search criteria.
            </div>
          ) : (
            filteredAvailablePanels.map(p => {
              const isAlreadyMapped = activelyMappedPanelIds.has(p.panelId);
              const isChecked = selectedPanelIds.includes(p.panelId);

              return (
                <div
                  key={p.panelId}
                  onClick={() => !isAlreadyMapped && togglePanelSelection(p.panelId)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.65rem 0.85rem',
                    backgroundColor: isAlreadyMapped
                      ? 'rgba(0, 0, 0, 0.03)'
                      : isChecked
                      ? 'rgba(14, 165, 233, 0.08)'
                      : 'var(--card-bg)',
                    border: isChecked
                      ? '1px solid var(--primary-color)'
                      : '1px solid var(--border-color)',
                    borderRadius: '6px',
                    cursor: isAlreadyMapped ? 'not-allowed' : 'pointer',
                    opacity: isAlreadyMapped ? 0.6 : 1,
                    transition: 'all 0.15s ease'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked || isAlreadyMapped}
                    disabled={isAlreadyMapped}
                    onChange={() => {}} // Handled by parent container click
                    style={{ marginTop: '0.2rem', cursor: isAlreadyMapped ? 'not-allowed' : 'pointer' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                        {p.panelName}
                      </span>
                      {isAlreadyMapped && (
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.45rem',
                          borderRadius: '4px',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: 600
                        }}>
                          Mapped
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.panelCode || `PNL-${p.panelId}`}</span>
                      <span>•</span>
                      <span style={{ textTransform: 'capitalize' }}>{p.panelType || 'Cash'}</span>
                      {p.contactMobile && (
                        <>
                          <span>•</span>
                          <span>📞 {p.contactMobile}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Selected Panels Basket / Tray */}
        <div style={{
          marginTop: '1rem',
          padding: '0.85rem 1rem',
          borderRadius: '8px',
          backgroundColor: 'rgba(14, 165, 233, 0.05)',
          border: '1px solid rgba(14, 165, 233, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.92rem', color: 'var(--primary-color)' }}>
                Selected Panels ({selectedPanelIds.length})
              </span>
              {selectedPanelIds.length === 0 && (
                <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Click panels above to select for mapping.
                </span>
              )}
            </div>

            {selectedPanelIds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginTop: '0.5rem', maxHeight: '72px', overflowY: 'auto' }}>
                {selectedPanelIds.map(pid => {
                  const pObj = allPanels.find(p => p.panelId === pid);
                  return (
                    <span
                      key={pid}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        padding: '0.2rem 0.55rem',
                        backgroundColor: '#fff',
                        borderRadius: '16px',
                        border: '1px solid var(--primary-color)',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: 'var(--text-primary)'
                      }}
                    >
                      <span>{pObj ? pObj.panelName : `Panel #${pid}`}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveChip(pid);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--danger-color)',
                          fontSize: '0.85rem',
                          lineHeight: 1,
                          padding: 0
                        }}
                        title="Remove panel"
                      >
                        ✕
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClearSelection}
              disabled={selectedPanelIds.length === 0 || savingMapping}
              style={{ padding: '0.55rem 1.15rem' }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveMapping}
              disabled={selectedPanelIds.length === 0 || !selectedCentreId || savingMapping}
              id="btn-save-centre-panel-mapping"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.55rem 1.35rem',
                fontWeight: 600
              }}
            >
              {savingMapping ? (
                <span>Saving Mapping...</span>
              ) : (
                <>
                  <span>✓</span>
                  <span>Save Mapping ({selectedPanelIds.length})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 3. Existing Mappings Data Grid */}
      <div className="glass-card" style={{ padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {selectedCentreObj?.centreName || 'Selected Centre'} – Mapped Commercial Panels
            </h3>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Active and historical mapping relationships for this laboratory location.
            </span>
          </div>

          {/* Grid Search & Filter */}
          <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search mapped panels..."
              value={mappingSearch}
              onChange={(e) => setMappingSearch(e.target.value)}
              style={{ width: '220px' }}
            />

            <select
              className="form-control"
              value={mappingStatusFilter}
              onChange={(e) => setMappingStatusFilter(e.target.value)}
              style={{ width: '130px' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={fetchMappings}
              title="Refresh Mappings"
              style={{ padding: '0.5rem 0.85rem' }}
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="master-table mvc-table" style={{ width: '100%', minWidth: '850px' }}>
            <thead>
              <tr>
                <th style={{ width: '60px' }}>#</th>
                <th style={{ width: '110px' }}>Panel Code</th>
                <th>Panel Name</th>
                <th style={{ width: '110px' }}>Type</th>
                <th style={{ width: '140px' }}>Contact</th>
                <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                <th style={{ width: '110px' }}>Valid From</th>
                <th style={{ width: '110px' }}>Valid To</th>
                <th style={{ width: '140px' }}>Created By</th>
                <th style={{ width: '160px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingMappings ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                    Loading centre panel mappings...
                  </td>
                </tr>
              ) : mappingPagination.totalRecords === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏢</div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                      No Panels Found
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', maxWidth: '400px', margin: '0 auto' }}>
                      {mappings.length === 0
                        ? `Select commercial panels from the assignment box above to map them to ${selectedCentreObj?.centreName || 'this centre'}.`
                        : 'No mappings match the current search filter.'}
                    </div>
                  </td>
                </tr>
              ) : (
                mappingPagination.paginatedItems.map((m, idx) => {
                  const sNo = (mappingPagination.page - 1) * mappingPagination.pageSize + idx + 1;
                  return (
                    <tr key={m.id} style={{ opacity: m.isActive ? 1 : 0.7 }}>
                      <td>{sNo}</td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {m.panelCode || `PNL-${m.panelId}`}
                      </td>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {m.panelName}
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.78rem',
                          fontWeight: 500,
                          backgroundColor: (m.panelType || '').toLowerCase() === 'corporate' ? '#f3e8ff' : '#f1f5f9',
                          color: (m.panelType || '').toLowerCase() === 'corporate' ? '#7e22ce' : '#334155'
                        }}>
                          {m.panelType || 'Cash'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        {m.contactMobile || m.contactEmail || '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`status-pill ${m.isActive ? 'active' : 'inactive'}`}>
                          {m.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {m.fromDate ? new Date(m.fromDate).toLocaleDateString('en-GB') : '-'}
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {m.toDate ? new Date(m.toDate).toLocaleDateString('en-GB') : (
                          <span style={{ color: 'var(--text-muted)' }}>Indefinite</span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        <div>{m.createdBy || 'System'}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {m.createdDate ? new Date(m.createdDate).toLocaleDateString('en-GB') : ''}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: '0.45rem', alignItems: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setDetailMapping(m)}
                            title="View Mapping Details & Audit"
                            style={{ padding: '0.25rem 0.55rem', fontSize: '0.75rem' }}
                          >
                            View
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(`/masters/panel-rates?panelId=${m.panelId}`)}
                            title="View / Configure Agreed Rate Card"
                            style={{ padding: '0.25rem 0.55rem', fontSize: '0.75rem', color: 'var(--primary-color)' }}
                          >
                            Rate Card
                          </button>

                          {m.isActive ? (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setConfirmAction({ type: 'deactivate', mapping: m })}
                              title="Deactivate mapping (retains historical traceability)"
                              style={{ padding: '0.25rem 0.55rem', fontSize: '0.75rem', color: 'var(--danger-color)' }}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setConfirmAction({ type: 'activate', mapping: m })}
                              title="Reactivate mapping"
                              style={{ padding: '0.25rem 0.55rem', fontSize: '0.75rem', color: 'var(--success-color)' }}
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Centralized Pagination */}
        <CommonPagination
          page={mappingPagination.page}
          pageSize={mappingPagination.pageSize}
          totalRecords={mappingPagination.totalRecords}
          onPageChange={mappingPagination.setPage}
          onPageSizeChange={mappingPagination.setPageSize}
          loading={loadingMappings}
        />
      </div>

      {/* Confirmation Modal for Activate / Deactivate */}
      <ConfirmModal
        isOpen={!!confirmAction}
        title={confirmAction?.type === 'deactivate' ? 'Deactivate Centre-Panel Mapping?' : 'Reactivate Centre-Panel Mapping?'}
        message={
          confirmAction?.type === 'deactivate'
            ? `Are you sure you want to deactivate mapping for "${confirmAction?.mapping?.panelName}" at "${selectedCentreObj?.centreName}"? Note: Historical orders and invoices will remain completely intact.`
            : `Reactivate commercial panel "${confirmAction?.mapping?.panelName}" for "${selectedCentreObj?.centreName}"?`
        }
        confirmLabel={confirmAction?.type === 'deactivate' ? 'Deactivate Mapping' : 'Activate Mapping'}
        variant={confirmAction?.type === 'deactivate' ? 'danger' : 'primary'}
        loading={actionProcessing}
        onConfirm={handleConfirmStatusChange}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Detail & Audit View Modal */}
      {detailMapping && (
        <div className="mst-modal-backdrop" onClick={() => setDetailMapping(null)}>
          <div
            className="mst-modal-card"
            style={{ maxWidth: '580px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem', marginBottom: '1rem' }}>
              <h3 className="mst-modal-title" style={{ margin: 0, fontSize: '1.15rem' }}>
                Centre-Panel Mapping Details
              </h3>
              <button
                type="button"
                onClick={() => setDetailMapping(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', fontSize: '0.88rem' }}>
              <div>
                <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.78rem' }}>Centre</span>
                <span style={{ fontWeight: 600 }}>{detailMapping.centreName} ({detailMapping.centreCode})</span>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.78rem' }}>Panel</span>
                <span style={{ fontWeight: 600 }}>{detailMapping.panelName} ({detailMapping.panelCode})</span>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.78rem' }}>Panel Type</span>
                <span>{detailMapping.panelType}</span>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.78rem' }}>Current Status</span>
                <span className={`status-pill ${detailMapping.isActive ? 'active' : 'inactive'}`}>
                  {detailMapping.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.78rem' }}>Effective From</span>
                <span>{detailMapping.fromDate ? new Date(detailMapping.fromDate).toLocaleDateString('en-GB') : 'Open'}</span>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.78rem' }}>Effective To</span>
                <span>{detailMapping.toDate ? new Date(detailMapping.toDate).toLocaleDateString('en-GB') : 'Indefinite / Ongoing'}</span>
              </div>
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
                <span style={{ fontWeight: 600, display: 'block', marginBottom: '0.35rem', color: 'var(--text-primary)' }}>
                  Audit & Traceability Log
                </span>
                <div style={{ background: 'var(--bg-secondary)', padding: '0.65rem 0.85rem', borderRadius: '6px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  <div>Created By: <strong>{detailMapping.createdBy || 'System'}</strong> on {detailMapping.createdDate ? new Date(detailMapping.createdDate).toLocaleString('en-GB') : '-'}</div>
                  {detailMapping.modifiedDate && (
                    <div style={{ marginTop: '0.25rem' }}>
                      Last Modified By: <strong>{detailMapping.modifiedBy || 'System'}</strong> on {new Date(detailMapping.modifiedDate).toLocaleString('en-GB')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mst-modal-footer" style={{ marginTop: '1.25rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setDetailMapping(null);
                  navigate(`/masters/panel-rates?panelId=${detailMapping.panelId}`);
                }}
              >
                Inspect Rate Card
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setDetailMapping(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentrePanelMappingPage;
